import type { ReactNode } from "react";
import { useLanguage } from "@/i18n/language";

/**
 * The karigar's balance, shown the same way everywhere it appears — as a
 * wallet card in the org's brand colour.
 *
 * One point is one QCoin, so the QCoin figure is spelled out underneath rather
 * than left as something to be explained by a counter. `tabular-nums` keeps the
 * digits from shifting as the number changes after a scan.
 *
 * `note` renders on a light strip below the brand face rather than on it: the
 * callers style it with ordinary page tokens (`text-muted`), which are chosen
 * to read on the page surface, not on an arbitrary brand fill.
 */
export function PointsBalanceCard({
  balance,
  loading,
  note,
}: {
  balance: number | null;
  loading: boolean;
  note?: ReactNode;
}) {
  const { t } = useLanguage("hi");
  return (
    <section className="overflow-hidden rounded-lg bg-raised shadow-pop ring-1 ring-black/5">
      <div className="relative overflow-hidden bg-brand px-5 pt-5 pb-5 text-on-brand">
        {/* Texture and a finder pattern bled off the corner — the product's
            mark, drawn in the on-brand colour so it suits any brand. */}
        <div
          aria-hidden="true"
          className="gq-modules pointer-events-none absolute inset-y-0 right-0 w-2/3 opacity-[0.12] [mask-image:linear-gradient(to_left,black,transparent)]"
        />
        {/* A whole finder pattern, sat where a bank card keeps its chip. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute top-5 right-5 size-11 rounded-[7px] border-[5px] border-current p-[5px] opacity-30"
        >
          <div className="size-full rounded-[2px] bg-current" />
        </div>
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-[linear-gradient(160deg,rgb(255_255_255/0.14),transparent_45%,rgb(0_0_0/0.12))]"
        />

        <div className="relative">
          <p className="text-sm font-medium opacity-85">{t("आपके अंक", "Your points")}</p>

          {loading || balance === null ? (
            <div className="mt-2 h-12 w-32 animate-pulse rounded-md bg-current opacity-20" />
          ) : (
            <p className="mt-1 font-title text-5xl leading-none font-semibold tracking-[-0.03em] tabular-nums">
              {balance}
            </p>
          )}

          <p className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-black/10 px-2.5 py-0.5 text-[13px] font-medium">
            {loading || balance === null ? "…" : `= ${balance} QCoin`}
          </p>
        </div>
      </div>

      {note ? <div className="flex justify-center px-5 pb-3 text-center">{note}</div> : null}
    </section>
  );
}
